import { getApiUrl } from '../config';
import { getEffectiveAuthToken } from './authToken';

async function adminRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(getApiUrl(`admin/${path}`), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getEffectiveAuthToken()}`,
      ...(init.headers || {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.message || 'Admin request failed');
  return body as T;
}

export const getAdminOverview = () => adminRequest<any>('overview');
export const getAdminRecipes = (query: URLSearchParams) => adminRequest<any>(`recipes?${query}`);
export const getAdminUsers = (query: URLSearchParams) => adminRequest<any>(`users?${query}`);
export const getAdminAuthEvents = (query: URLSearchParams) => adminRequest<any>(`auth-events?${query}`);
export const getAdminEmails = (query: URLSearchParams) => adminRequest<any>(`emails?${query}`);
export const getAdminUser = (id: string) => adminRequest<any>(`users/${id}`);
export const setAdminUserStatus = (id: string, status: 'active' | 'suspended') =>
  adminRequest<any>(`users/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
export const resetAdminUser = (id: string) =>
  adminRequest<any>(`users/${id}/reset`, { method: 'POST' });
export const startAdminImpersonation = (id: string) =>
  adminRequest<{ token: string; expiresAt: string; target: { id: string; email: string } }>(
    `users/${id}/impersonation`,
    { method: 'POST' }
  );
export const getAdminExecutions = (query: URLSearchParams) => adminRequest<any>(`executions?${query}`);
export const getAdminExecution = (id: string) => adminRequest<any>(`executions/${id}`);
export const closePausedAdminExecution = (id: string) =>
  adminRequest<{ id: string; status: string }>(`executions/${id}/close-paused`, { method: 'POST' });
export const replayAdminWebhookDelivery = (id: string, reason?: string) =>
  adminRequest<{ deliveryId: string; status: string }>(`webhook-deliveries/${id}/replay`, {
    method: 'POST', body: JSON.stringify({ reason: reason?.trim() || undefined }),
  });
export const getAdminRuntimeHealth = (query: URLSearchParams) => adminRequest<any>(`runtime-health?${query}`);
export const getAdminCosts = (query: URLSearchParams) => adminRequest<any>(`costs?${query}`);
export const getRecipeExecutionSettings = () => adminRequest<any>('settings/recipe-execution');
export const updateRecipeExecutionSettings = (settings: {
  model: string;
  reasoningEffort: string;
  autoCompilationThresholds: { manual: number; schedule: number; webhook: number };
}) => adminRequest<any>('settings/recipe-execution', { method: 'PUT', body: JSON.stringify(settings) });
export const requestRecipeCompilation = (recipeId: number) =>
  adminRequest<{ implementationId: string; status: string }>(`recipes/${recipeId}/compile`, { method: 'POST' });

export const getBillingProfiles = () => adminRequest<{ items: any[] }>('billing/profiles');
export const createBillingProfile = (profile: Record<string, unknown>) =>
  adminRequest<any>('billing/profiles', { method: 'POST', body: JSON.stringify(profile) });
export const getAdminUserBilling = (userId: string) => adminRequest<any>(`users/${userId}/billing`);
export const assignAdminUserBilling = (userId: string, input: { profileId: string; effectiveAt?: string; expiresAt?: string | null; note?: string }) =>
  adminRequest<any>(`users/${userId}/billing-assignment`, { method: 'POST', body: JSON.stringify(input) });
export const removeAdminUserBillingAssignment = (userId: string) =>
  adminRequest<any>(`users/${userId}/billing-assignment`, { method: 'DELETE' });

export type AdminApiTokenRecord = {
  id: string;
  name: string;
  tokenPrefix: string;
  creatorUserId: string;
  creatorEmail: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export const getAdminApiTokens = () => adminRequest<{ items: AdminApiTokenRecord[] }>('api-tokens');
export const createAdminApiToken = (name: string) =>
  adminRequest<AdminApiTokenRecord & { secret: string }>('api-tokens', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
export const revokeAdminApiToken = (id: string) =>
  adminRequest<{ id: string; revokedAt: string }>(`api-tokens/${id}`, { method: 'DELETE' });
export const getAdminApiDiscoveryUrl = () => getApiUrl('').replace(/\/$/, '');

export type MasterChefApprovalRecord = {
  id: string;
  actorUserId: string;
  actorEmail: string | null;
  conversationId: string;
  command: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'running' | 'succeeded' | 'failed' | 'expired';
  output: string | null;
  errorMessage: string | null;
  expiresAt: string;
  decidedAt: string | null;
  completedAt: string | null;
  createdAt: string;
};

export const getMasterChefApprovals = () => adminRequest<{
  commandExecutionEnabled: boolean;
  items: MasterChefApprovalRecord[];
}>('master-chef/approvals');

export const decideMasterChefApproval = (id: string, approve: boolean) =>
  adminRequest<{ id: string; status: string; output: string | null; errorMessage: string | null }>(
    `master-chef/approvals/${id}/decision`,
    { method: 'POST', body: JSON.stringify({ approve }) }
  );

export type MasterChefMfaStatus = {
  configured: boolean;
  enrolled: boolean;
  confirmedAt: string | null;
  pendingEnrollmentExpiresAt: string | null;
  grantActive: boolean;
  grantExpiresAt: string | null;
};

export type MasterChefMfaEnrollment = {
  qrDataUrl: string;
  manualKey: string;
  expiresAt: string;
};

export const getMasterChefMfaStatus = () =>
  adminRequest<MasterChefMfaStatus>('master-chef/mfa');
export const beginMasterChefMfaEnrollment = () =>
  adminRequest<MasterChefMfaEnrollment>('master-chef/mfa/enrollment', { method: 'POST' });
export const confirmMasterChefMfaEnrollment = (code: string) =>
  adminRequest<MasterChefMfaStatus>('master-chef/mfa/enrollment/confirm', {
    method: 'POST', body: JSON.stringify({ code }),
  });
export const resetMasterChefMfa = () =>
  adminRequest<{ reset: true }>('master-chef/mfa', { method: 'DELETE' });
